"use client";

import React, { useMemo, useRef, useState } from "react";
import { uploadFileWithProgress } from "../../lib/api";
import { Button } from "../ui/button";
import { BannerHeader } from "./BannerHeader";
import { SubmissionDateSection } from "./SubmissionDateSection";
import { ProductInfoSection } from "./ProductInfoSection";
import { UploadsSection } from "./UploadsSection";
import { SupplierSection } from "./SupplierSection";
import { FinalMessageSection } from "./FinalMessageSection";
import type { FileBuckets, UploadEntry, UploadStateMap } from "./types";
import { debugArray } from "../../lib/debugArray";
import { isEmptyRichTextHtml } from "../../lib/richText";

function getTodayLocalDateString() {
  const now = new Date();
  const paddedDay = String(now.getDate()).padStart(2, "0");
  const paddedMonth = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${paddedMonth}-${paddedDay}`;
}

function normalizeRichTextHtml(value: string) {
  const trimmed = value.trim();
  if (isEmptyRichTextHtml(trimmed)) return "";
  if (/^<\/?[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return `<p>${trimmed.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br />")}</p>`;
}

function createLocalId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createEmptyUploadState(): UploadStateMap {
  return {
    product_pictures: [],
    product_3d_files: [],
    user_manual: [],
    certificates: [],
    packaging_diecut_artwork: [],
    outline_images: [],
    price_structure: [],
    product_video: [],
    supplier_company_profile: [],
  };
}

const initialFileBuckets: FileBuckets = {
  product_pictures: [],
  product_3d_files: [],
  user_manual: [],
  certificates: [],
  packaging_diecut_artwork: [],
  outline_images: [],
  price_structure: [],
  product_video: [],
  supplier_company_profile: [],
};

const requiredFields = [
  { name: "product_model", label: "Product Model Name or Code" },
  { name: "product_overview", label: "Product Description and Overview" },
  { name: "product_specs", label: "Product Technical Specs" },
  { name: "product_features", label: "Product Features and Compatibility" },
  { name: "materials", label: "Made of Materials" },
  { name: "packaging_content", label: "Retails Packaging Content" },
  { name: "major_markets", label: "Major Markets (Countries)" },
  { name: "brands_buying", label: "Brand Names that are buying this product" },
  { name: "supplier_company_name", label: "Supplier Company Name" },
  { name: "supplier_contact_name", label: "Supplier Contact Person Name" },
  { name: "supplier_contact_email", label: "Supplier Contact Person Email ID" },
  { name: "supplier_contact_mobile_wechat", label: "Supplier Contact Person Mobile Number and Wechat ID" },
];

const requiredBuckets = [
  { key: "product_pictures", label: "Product Pictures" },
  { key: "product_3d_files", label: "Product 3D drawing files" },
  { key: "user_manual", label: "User Manual" },
  { key: "certificates", label: "Certificates" },
  { key: "packaging_diecut_artwork", label: "Packaging Die-cut and Artwork files" },
  { key: "outline_images", label: "Outline images" },
  { key: "price_structure", label: "Price Structure" },
  { key: "product_video", label: "Product Supporting Files" },
  { key: "supplier_company_profile", label: "Supplier Company Profile" },
];

const MAX_FILES_PER_SECTION = 10;
const MAX_TOTAL_BYTES_PER_SECTION = 30 * 1024 * 1024;

type SubmissionPhase = "idle" | "uploading" | "submitting" | "failed";

type ToastState = {
  kind: "error";
  message: string;
} | null;

type LimitAlertState = {
  title: string;
  message: string;
} | null;

function reconcileEntries(prevEntries: UploadEntry[], nextFiles: File[]) {
  const remaining = [...prevEntries];
  return nextFiles.map((file) => {
    const idx = remaining.findIndex(
      (entry) =>
        entry.file === file ||
        (entry.file.name === file.name &&
          entry.file.size === file.size &&
          entry.file.lastModified === file.lastModified),
    );

    if (idx >= 0) {
      const [matched] = remaining.splice(idx, 1);
      return matched;
    }

    return {
      localId: createLocalId(),
      file,
      status: "queued" as const,
      progress: 0,
    };
  });
}

function formatUploadBytes(bytes: number) {
  const mb = bytes / (1024 * 1024);
  if (Number.isInteger(mb)) return `${mb} MB`;
  return `${mb.toFixed(1)} MB`;
}

function buildBucketLimitMessage(label: string, files: File[]) {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const details: string[] = [];

  if (files.length > MAX_FILES_PER_SECTION) {
    details.push(
      `You selected ${files.length} files, but this section allows only ${MAX_FILES_PER_SECTION}.`,
    );
  }

  if (totalBytes > MAX_TOTAL_BYTES_PER_SECTION) {
    details.push(
      `The total size is ${formatUploadBytes(totalBytes)}, but this section allows only ${formatUploadBytes(MAX_TOTAL_BYTES_PER_SECTION)}.`,
    );
  }

  return {
    title: `${label} upload limit reached`,
    message: `${details.join(" ")} Max size support is 30 MB per section and max number of files is 10 per section. Please remove some files and try again.`,
  };
}

function ProgressModal({
  open,
  phase,
  uploadedCount,
  totalCount,
  activeLabel,
  status,
  onClose,
}: {
  open: boolean;
  phase: SubmissionPhase;
  uploadedCount: number;
  totalCount: number;
  activeLabel: string | null;
  status: string | null;
  onClose: () => void;
}) {
  if (!open) return null;

  const progress = totalCount ? Math.round((uploadedCount / totalCount) * 100) : 0;
  const isFailed = phase === "failed";
  const title =
    phase === "uploading"
      ? "Uploading files"
      : phase === "submitting"
        ? "Submitting record"
        : "Submission stopped";

  const subtitle =
    phase === "uploading"
      ? activeLabel || "Please wait while your files upload."
      : phase === "submitting"
        ? "All files are uploaded. Creating the record now."
        : status || "Submission failed.";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 px-4">
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Upload status</div>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">{title}</h2>
            <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm font-medium text-slate-700">
              <span>
                {uploadedCount} / {totalCount} files
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-200">
              <div
                className={[
                  "h-full rounded-full transition-all",
                  isFailed ? "bg-red-500" : "bg-emerald-500",
                ].join(" ")}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {isFailed ? (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              Uploaded files are kept temporarily. The user can retry submit without uploading the same files again.
            </div>
          ) : null}

          {isFailed ? (
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={onClose}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Close
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function SubmitPageClient() {
  const formRef = useRef<HTMLFormElement>(null);
  const submissionLockRef = useRef(false);
  const [status, setStatus] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [invalidBuckets, setInvalidBuckets] = useState<string[]>([]);
  const [limitAlert, setLimitAlert] = useState<LimitAlertState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionPhase, setSubmissionPhase] = useState<SubmissionPhase>("idle");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [activeUploadLabel, setActiveUploadLabel] = useState<string | null>(null);

  const [fileBuckets, setFileBuckets] = useState<FileBuckets>(initialFileBuckets);
  const [uploadState, setUploadState] = useState<UploadStateMap>(createEmptyUploadState());
  const [stagedUploadSessionId, setStagedUploadSessionId] = useState<string | null>(null);
  const [formResetVersion, setFormResetVersion] = useState(0);

  const uploadSummary = useMemo(() => {
    const entries = Object.values(uploadState).flat();
    const totalCount = entries.length;
    const uploadedCount = entries.filter((entry) => entry.status === "uploaded").length;
    const inProgress = entries.some((entry) => entry.status === "uploading" || entry.status === "removing");
    return { totalCount, uploadedCount, inProgress };
  }, [uploadState]);

  React.useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function setBucket(key: string, files: File[]) {
    const bucketLabel = requiredBuckets.find((bucket) => bucket.key === key)?.label || key;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

    if (files.length > MAX_FILES_PER_SECTION || totalBytes > MAX_TOTAL_BYTES_PER_SECTION) {
      setLimitAlert(buildBucketLimitMessage(bucketLabel, files));
      return;
    }

    setLimitAlert(null);
    setFileBuckets((prev) => ({ ...prev, [key]: files }));
    setUploadState((prev) => ({
      ...prev,
      [key]: reconcileEntries(prev[key] || [], files),
    }));

    if (files.length > 0) {
      setInvalidBuckets((prev) => prev.filter((item) => item !== key));
      const bucketLabel = requiredBuckets.find((bucket) => bucket.key === key)?.label;
      if (bucketLabel) {
        setValidationErrors((prev) => prev.filter((item) => item !== bucketLabel));
      }
    }
  }

  function handleLimitExceeded(title: string, message: string) {
    setLimitAlert({ title, message });
  }

  function updateUploadEntry(key: string, localId: string, updater: (entry: UploadEntry) => UploadEntry) {
    setUploadState((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((entry) => (entry.localId === localId ? updater(entry) : entry)),
    }));
  }

  async function handleRemoveFile(key: string, idx: number) {
    const entry = uploadState[key]?.[idx];
    if (!entry) return;

    if (entry.uploadSessionId && entry.uploadId) {
      updateUploadEntry(key, entry.localId, (current) => ({ ...current, status: "removing" }));
      try {
        if (entry.uploadSessionId && entry.uploadId) {
          const res = await fetch("/api/upload-local", {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              session_id: entry.uploadSessionId,
              upload_id: entry.uploadId,
            }),
          });

          if (!res.ok) {
            const payload = (await res.json().catch(() => null)) as { error?: string } | null;
            throw new Error(payload?.error || "Failed to remove uploaded file");
          }
        }
      } catch (err: any) {
        updateUploadEntry(key, entry.localId, (current) => ({
          ...current,
          status: "error",
          error: err?.message || "Failed to remove uploaded file",
        }));
        return;
      }
    }

    const nextFiles = (fileBuckets[key] || []).filter((_, fileIdx) => fileIdx !== idx);
    setBucket(key, nextFiles);
  }

  async function handleClear() {
    if (isSubmitting || !formRef.current) return;

    if (stagedUploadSessionId) {
      await fetch("/api/upload-local", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: stagedUploadSessionId }),
      }).catch(() => null);
    } else {
      const uploadedEntries = Object.values(uploadState).flat().filter((entry) => entry.uploadSessionId && entry.uploadId);
      await Promise.allSettled(
        uploadedEntries.map(async (entry) => {
          if (!entry.uploadSessionId || !entry.uploadId) return;
          await fetch("/api/upload-local", {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ session_id: entry.uploadSessionId, upload_id: entry.uploadId }),
          });
        }),
      );
    }

    formRef.current.reset();
    // Native form reset does not clear TipTap's internal editor state. Remounting
    // the rich-text sections gives every editor a fresh, empty document.
    setFormResetVersion((version) => version + 1);
    setFileBuckets(initialFileBuckets);
    setUploadState(createEmptyUploadState());
    setStagedUploadSessionId(null);
    setStatus(null);
    setToast(null);
    setLimitAlert(null);
    setValidationErrors([]);
    setInvalidFields([]);
    setInvalidBuckets([]);
    setSubmissionPhase("idle");
    setShowProgressModal(false);
    setActiveUploadLabel(null);
  }

  function handleFieldBlur(e: React.FocusEvent<HTMLFormElement>) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (!target.name || !target.required || target.disabled) return;

    const value = target.value.trim();
    if (!value) {
      setInvalidFields((prev) => (prev.includes(target.name) ? prev : [...prev, target.name]));
    }
  }

  function handleFieldChange(e: React.FormEvent<HTMLFormElement>) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (!target.name) return;

    if (target.value.trim()) {
      setInvalidFields((prev) => prev.filter((item) => item !== target.name));
      const fieldLabel = requiredFields.find((field) => field.name === target.name)?.label;
      if (fieldLabel) {
        setValidationErrors((prev) => prev.filter((item) => item !== fieldLabel));
      }
    }
  }

  async function ensureUploadsCompleted() {
    let currentSessionId = stagedUploadSessionId;
    const pendingUploads = requiredBuckets.flatMap((bucket) =>
      (uploadState[bucket.key] || [])
        .filter((entry) => entry.status !== "uploaded")
        .map((entry) => ({ bucket, entry })),
    );

    if (!currentSessionId) {
      currentSessionId = createLocalId();
      setStagedUploadSessionId(currentSessionId);
    }

    const uploadOne = async ({ bucket, entry }: (typeof pendingUploads)[number]) => {
      setActiveUploadLabel(`${bucket.label}: ${entry.file.name}`);
      updateUploadEntry(bucket.key, entry.localId, (current) => ({
        ...current,
        status: "uploading",
        progress: current.progress || 0,
        error: undefined,
      }));

      try {
        const data = await uploadFileWithProgress({
          path: "/api/upload-local",
          file: entry.file,
          sessionId: currentSessionId,
          extraFields: { category: bucket.key },
          useBaseUrl: false,
          onProgress: (progress) => {
            updateUploadEntry(bucket.key, entry.localId, (current) => ({
              ...current,
              status: "uploading",
              progress,
            }));
          },
        });

        const nextSessionId =
          typeof data?.session_id === "string" && data.session_id.trim()
            ? data.session_id.trim()
            : currentSessionId;

        updateUploadEntry(bucket.key, entry.localId, (current) => ({
          ...current,
          status: "uploaded",
          progress: 100,
          error: undefined,
          storageProvider: data?.provider || undefined,
          storagePath: data?.upload?.path,
          uploadId: data?.upload?.upload_id,
          uploadSessionId: nextSessionId,
        }));
      } catch (err: any) {
        updateUploadEntry(bucket.key, entry.localId, (current) => ({
          ...current,
          status: "error",
          error: err?.message || "Upload failed",
        }));
        throw err;
      }
    };

    // Four concurrent requests improve throughput without overwhelming the API
    // or the WebDAV server with dozens of simultaneous connections.
    const workerCount = Math.min(4, pendingUploads.length);
    let nextIndex = 0;
    let firstError: unknown = null;
    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        while (nextIndex < pendingUploads.length) {
          const upload = pendingUploads[nextIndex++];
          try {
            await uploadOne(upload);
          } catch (error) {
            firstError ??= error;
          }
        }
      }),
    );

    if (firstError) throw firstError;
    return currentSessionId;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submissionLockRef.current || isSubmitting) return;

    if (uploadSummary.inProgress) {
      setToast({ kind: "error", message: "Files are still uploading. Please wait until all uploads finish before submitting." });
      return;
    }

    setStatus(null);
    setToast(null);
    setValidationErrors([]);
    setInvalidFields([]);
    setInvalidBuckets([]);

    const form = new FormData(e.currentTarget);

    const errors: string[] = [];
    const invalidFieldNames: string[] = [];
    const missingBuckets: string[] = [];

    const submissionDate = (form.get("submission_date") || "").toString().trim();
    if (!submissionDate) {
      errors.push("Submission Date");
      invalidFieldNames.push("submission_date_picker");
    } else if (submissionDate !== getTodayLocalDateString()) {
      errors.push("Submission Date must be today's date");
      invalidFieldNames.push("submission_date_picker");
    }

    requiredFields.forEach((field) => {
      const value = (form.get(field.name) || "").toString().trim();
      if (!value) {
        errors.push(field.label);
        invalidFieldNames.push(field.name);
      }
    });

    requiredBuckets.forEach((bucket) => {
      const files = fileBuckets[bucket.key] || [];
      if (files.length === 0) {
        errors.push(bucket.label);
        missingBuckets.push(bucket.key);
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      setInvalidFields(invalidFieldNames);
      setInvalidBuckets(missingBuckets);
      return;
    }

    try {
      // This ref is set synchronously, preventing rapid clicks from starting
      // another upload/submission before React can disable the button.
      submissionLockRef.current = true;
      setIsSubmitting(true);
      setShowProgressModal(true);
      setSubmissionPhase("uploading");

      const currentSessionId = await ensureUploadsCompleted();

      setSubmissionPhase("submitting");
      setActiveUploadLabel(null);

      const payload = {
        submission_date: submissionDate,
        product_model: (form.get("product_model") || "").toString().trim(),
        product_overview: normalizeRichTextHtml((form.get("product_overview") || "").toString()),
        product_specs: normalizeRichTextHtml((form.get("product_specs") || "").toString()),
        product_features: normalizeRichTextHtml((form.get("product_features") || "").toString()),
        materials: normalizeRichTextHtml((form.get("materials") || "").toString()),
        packaging_content: normalizeRichTextHtml((form.get("packaging_content") || "").toString()),
        major_markets: normalizeRichTextHtml((form.get("major_markets") || "").toString()),
        brands_buying: normalizeRichTextHtml((form.get("brands_buying") || "").toString()),
        supplier_company_name: (form.get("supplier_company_name") || "").toString().trim(),
        supplier_contact_name: (form.get("supplier_contact_name") || "").toString().trim(),
        supplier_contact_email: (form.get("supplier_contact_email") || "").toString().trim(),
        supplier_contact_mobile_wechat: (form.get("supplier_contact_mobile_wechat") || "").toString().trim(),
        final_message: normalizeRichTextHtml((form.get("final_message") || "").toString()),
      };
      if (currentSessionId) {
        Object.assign(payload, { staged_upload_session_id: currentSessionId });
      }

      const requestBody = JSON.stringify(payload);
      const submitRequest = () => fetch("/api/submit-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestBody,
      });

      let res = await submitRequest();
      // A proxy can time out after the API has committed the request. The API
      // treats the staged upload session as idempotent, so briefly retrying lets
      // the user receive the already-created reference number automatically.
      for (let attempt = 0; attempt < 3 && [409, 502, 503, 504].includes(res.status); attempt += 1) {
        setActiveUploadLabel("Confirming your submission...");
        await new Promise((resolve) => window.setTimeout(resolve, 1500 * (attempt + 1)));
        res = await submitRequest();
      }

      if (!res.ok && currentSessionId) {
        // The create request may have committed even when the proxy timed out.
        // Polling this lightweight endpoint gives the user a definitive answer
        // without risking another duplicate submission.
        for (let attempt = 0; attempt < 4; attempt += 1) {
          setActiveUploadLabel("Checking your submission status...");
          const confirmation = await fetch(
            `/api/submit-request?session_id=${encodeURIComponent(currentSessionId)}`,
          );
          const confirmationData = (await confirmation.json().catch(() => null)) as {
            reference_no?: string | null;
          } | null;

          if (confirmation.ok && confirmationData?.reference_no) {
            window.location.href = `/submit/success?ref=${encodeURIComponent(confirmationData.reference_no)}`;
            return;
          }

          await new Promise((resolve) => window.setTimeout(resolve, 1500 * (attempt + 1)));
        }
      }

      const data = (await res.json().catch(() => null)) as { reference_no?: string; error?: string } | null;
      if (!res.ok) {
        throw new Error(data?.error || "Submission failed");
      }
      window.location.href = `/submit/success?ref=${data.reference_no}`;
    } catch (err: any) {
      const message =
        err?.message ||
        "Submission failed. Uploaded files are still available for retry, so the user does not need to upload them again.";
      setStatus(message);
      setToast({ kind: "error", message });
      setSubmissionPhase("failed");
      setShowProgressModal(true);
      setIsSubmitting(false);
      submissionLockRef.current = false;
    }
  }

  return (
    <main className="min-h-screen overflow-x-clip bg-[rgb(var(--rf-bg))] px-3 py-4 sm:px-4 sm:py-8">
      {limitAlert ? (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/70 px-4 py-6"
          role="presentation"
          onClick={() => setLimitAlert(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-white/15 bg-white p-6 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-limit-title"
            aria-describedby="upload-limit-message"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="upload-limit-title" className="text-lg font-semibold text-slate-900">
                  {limitAlert.title}
                </h2>
                <p id="upload-limit-message" className="mt-2 text-sm leading-6 text-slate-600">
                  {limitAlert.message}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                onClick={() => setLimitAlert(null)}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Okay
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed inset-x-3 top-3 z-[70] sm:inset-x-auto sm:right-4 sm:top-4 sm:w-full sm:max-w-md">
          <div className="rounded-2xl border border-red-500/60 bg-red-950 px-4 py-3 text-sm text-red-100 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">Submission Error</div>
                <p className="mt-1 text-red-100/90">{toast.message}</p>
              </div>
              <button
                type="button"
                onClick={() => setToast(null)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-red-100/80 hover:bg-red-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ProgressModal
        open={showProgressModal}
        phase={submissionPhase}
        uploadedCount={uploadSummary.uploadedCount}
        totalCount={uploadSummary.totalCount}
        activeLabel={activeUploadLabel}
        status={status}
        onClose={() => {
          if (!isSubmitting) {
            setShowProgressModal(false);
          }
        }}
      />

      <div className="mx-auto w-full min-w-0 max-w-5xl space-y-4 sm:space-y-6">
        <BannerHeader />

        {validationErrors.length > 0 ? (
          <div className="fixed inset-x-3 top-3 z-50 rounded-2xl border border-red-500/60 bg-[#2a1030] p-4 text-sm text-red-200 shadow-lg sm:inset-x-auto sm:right-4 sm:top-4 sm:w-full sm:max-w-sm">
            <div className="font-semibold">Please fill all required fields</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {debugArray("submit.validationErrors", validationErrors).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          onBlurCapture={handleFieldBlur}
          onInputCapture={handleFieldChange}
          className="min-w-0 space-y-6"
          noValidate
        >
          <SubmissionDateSection invalidFields={invalidFields} />
          <ProductInfoSection key={`product-info-${formResetVersion}`} invalidFields={invalidFields} />
          <UploadsSection
            fileBuckets={fileBuckets}
            setBucket={setBucket}
            uploadState={uploadState}
            onRemoveFile={handleRemoveFile}
            disablePicker={uploadSummary.inProgress || isSubmitting}
            invalidBuckets={invalidBuckets}
            onLimitExceeded={handleLimitExceeded}
          />
          <SupplierSection
            fileBuckets={fileBuckets}
            setBucket={setBucket}
            uploadState={uploadState}
            onRemoveFile={handleRemoveFile}
            disablePicker={uploadSummary.inProgress || isSubmitting}
            invalidBuckets={invalidBuckets}
            invalidFields={invalidFields}
            onLimitExceeded={handleLimitExceeded}
          />
          <FinalMessageSection key={`final-message-${formResetVersion}`} />

          <div className="rounded-2xl border border-indigo-700/70 bg-indigo-950/60 p-4 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  type="button"
                  onClick={handleClear}
                  disabled={isSubmitting}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-7 text-base font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9zm-1 12a2 2 0 0 1-2-2V8h16v11a2 2 0 0 1-2 2H6z"
                    />
                  </svg>
                  Clear
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || uploadSummary.inProgress}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-7 text-base font-semibold text-white hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                    />
                  </svg>
                  {isSubmitting ? "Processing..." : uploadSummary.inProgress ? "Uploading files..." : "Submit"}
                </Button>
              </div>
              <div className="space-y-1 sm:text-right">
                <p className="text-sm text-emerald-200">
                  {uploadSummary.totalCount
                    ? `${uploadSummary.uploadedCount} of ${uploadSummary.totalCount} files uploaded`
                    : "No files selected"}
                </p>
                {status ? <p className="text-sm text-red-300">{status}</p> : null}
              </div>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}
